import React, { useEffect, useState } from "react";
import { Bell, BellOff, Smartphone, Check, PackageCheck, Sun } from "lucide-react";
import { toast } from "sonner";
import { sendTestPush, setHandoffPush, setDailyNudge } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { getPushState, enablePush, disablePush, pushSupported, isIosNeedsInstall, getCurrentEndpoint } from "../lib/push";

export function DeliveryCard() {
  const { user, refresh } = useAuth();
  const hasPartner = Boolean(user?.partner);
  const partnerName = user?.partner?.name || "your partner";
  const handoffOn = Boolean(user?.handoff_push_enabled);
  const nudgeOn = Boolean(user?.daily_nudge_enabled);
  const [push, setPush] = useState({ supported: false, permission: "unsupported", subscribed: false });
  const [iosInstall, setIosInstall] = useState(false);
  const [busy, setBusy] = useState(false);

  const refreshPush = () => getPushState().then(setPush).catch(() => {});

  useEffect(() => {
    refreshPush();
    setIosInstall(isIosNeedsInstall());
  }, []);

  // Handoff-completion push opt-in. The explanation is always shown above; clicking Enable
  // is the explicit accept. The OS prompt only fires here, never cold. [Not now] = don't click.
  const enableHandoff = async () => {
    setBusy(true);
    try {
      const granted = push.subscribed && push.permission === "granted";
      if (!granted) {
        const res = await enablePush();
        if (!res.granted) {
          toast(push.permission === "denied"
            ? "Notifications are blocked in your browser settings."
            : "Notifications weren't enabled.");
          return;
        }
      }
      await setHandoffPush(true);
      await refresh();
      toast.success(`On. I'll let you know when ${partnerName} handles something.`);
    } catch (e) {
      toast(e.message === "push-disabled" ? "Push isn't configured on the server." : "Couldn't turn this on.");
    } finally { await refreshPush(); setBusy(false); }
  };

  const disableHandoff = async () => {
    setBusy(true);
    try { await setHandoffPush(false); await refresh(); toast("Turned off. Your other notifications stay on."); }
    catch (_) { toast("Couldn't update that."); }
    finally { setBusy(false); }
  };

  // Daily nudge opt-in (default off). Needs push permission first; reuses the same enablePush flow.
  const enableNudge = async () => {
    setBusy(true);
    try {
      const granted = push.subscribed && push.permission === "granted";
      if (!granted) {
        const res = await enablePush();
        if (!res.granted) {
          toast(push.permission === "denied"
            ? "Notifications are blocked in your browser settings."
            : "Notifications weren't enabled.");
          return;
        }
      }
      await setDailyNudge(true);
      await refresh();
      toast.success("On. I'll bring you a calm summary each morning.");
    } catch (e) {
      toast(e.message === "push-disabled" ? "Push isn't configured on the server." : "Couldn't turn this on.");
    } finally { await refreshPush(); setBusy(false); }
  };

  const disableNudge = async () => {
    setBusy(true);
    try { await setDailyNudge(false); await refresh(); toast("Daily nudge turned off."); }
    catch (_) { toast("Couldn't update that."); }
    finally { setBusy(false); }
  };

  const turnOn = async () => {
    setBusy(true);
    try {
      const res = await enablePush();
      if (res.granted) toast.success("Notifications on. I'll bring things back to you.");
      else if (push.permission === "denied") toast("Notifications are blocked in your browser settings.");
      else toast("Notifications weren't enabled.");
    } catch (e) {
      toast(e.message === "push-disabled" ? "Push isn't configured on the server." : "Couldn't turn on notifications.");
    } finally { await refreshPush(); setBusy(false); }
  };

  const turnOff = async () => {
    setBusy(true);
    await disablePush();
    await refreshPush();
    toast("Notifications turned off for this device.");
    setBusy(false);
  };

  const testNudge = async () => {
    setBusy(true);
    try {
      const endpoint = await getCurrentEndpoint();
      await sendTestPush(endpoint);
      toast.success("Test nudge sent. Check your notifications.");
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast(msg || "Couldn't send a test nudge. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-3xl bg-[var(--surface)] border border-[var(--line)] shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-5 md:p-6" data-testid="delivery-card">
      <h2 className="font-serif-display text-lg text-[var(--ink)] mb-1">How Heldall reaches you</h2>
      <p className="text-sm text-[var(--ink3)] mb-5">So things come back to you without having to remember to open the app.</p>

      {/* Push — primary channel */}
      <div className="rounded-2xl border border-[var(--line2)] bg-[var(--surface2)] p-4">
        <div className="flex items-start gap-3">
          <span className="h-9 w-9 rounded-full bg-[var(--pine-soft)] flex items-center justify-center text-[var(--pine)] shrink-0">
            <Smartphone size={17} strokeWidth={1.9} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[15px] font-semibold text-[var(--ink)]">Push notifications</p>
            <p className="text-[13px] text-[var(--ink3)] mt-0.5" data-testid="push-status">
              {!push.supported
                ? "This browser doesn't support notifications."
                : push.subscribed && push.permission === "granted"
                  ? "On for this device. I'll nudge you when something's due."
                  : push.permission === "denied"
                    ? "Blocked. Enable notifications in your browser/site settings, then reload."
                    : "Get a gentle nudge when a reminder is due. No noise, nothing else."}
            </p>

            {iosInstall && (
              <div className="mt-3 rounded-xl bg-[var(--surface)] border border-[var(--line2)] px-3 py-2.5" data-testid="ios-install-nudge">
                <p className="text-[13px] text-[var(--ink2)] leading-relaxed">
                  <span className="font-semibold text-[var(--ink)]">On iPhone:</span> tap the Share icon →
                  <span className="font-semibold"> Add to Home Screen</span>, then open Heldall from your Home Screen to turn on notifications.
                </p>
                <p className="text-[11px] text-[var(--ink5)] mt-1">iPhone notifications are still being validated.</p>
              </div>
            )}

            {push.supported && !iosInstall && (
              <div className="mt-3 flex flex-col sm:flex-row items-stretch gap-2">
                {push.subscribed && push.permission === "granted" ? (
                  <>
                    <button data-testid="push-test-btn" onClick={testNudge} disabled={busy}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] text-[var(--on-pine)] px-4 py-2 text-sm font-semibold transition-all disabled:opacity-50 shadow-[0_4px_14px_rgba(44,90,77,0.20)]">
                      <Bell size={14} strokeWidth={2} /> Send test nudge
                    </button>
                    <button data-testid="push-disable-btn" onClick={turnOff} disabled={busy}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[var(--surface)] border border-[var(--line)] px-4 py-2 text-sm font-semibold text-[var(--ink2)] hover:border-[var(--pine)] hover:text-[var(--pine)] transition-all disabled:opacity-50">
                      <BellOff size={14} strokeWidth={2} /> Turn off on this device
                    </button>
                  </>
                ) : push.permission !== "denied" ? (
                  <button data-testid="push-enable-btn" onClick={turnOn} disabled={busy}
                    className="w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] text-[var(--on-pine)] px-4 py-2 text-sm font-semibold transition-all disabled:opacity-50 shadow-[0_4px_14px_rgba(44,90,77,0.20)]">
                    <Bell size={14} strokeWidth={2} /> Turn on notifications
                  </button>
                ) : null}
              </div>
            )}
          </div>
          {push.subscribed && push.permission === "granted" && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[var(--pine)] bg-[var(--pine-soft)] rounded-full px-2 py-0.5 shrink-0">
              <Check size={11} strokeWidth={2.4} /> On
            </span>
          )}
        </div>
      </div>

      {/* Daily nudge — calm once-a-day morning summary. Available to everyone. */}
      <div className="mt-4 rounded-2xl border border-[var(--line2)] bg-[var(--surface2)] p-4" data-testid="daily-nudge-section">
        <div className="flex items-start gap-3">
          <span className="h-9 w-9 rounded-full bg-[var(--pine-soft)] flex items-center justify-center text-[var(--pine)] shrink-0">
            <Sun size={17} strokeWidth={1.9} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[15px] font-semibold text-[var(--ink)]">Daily nudge</p>
            <p className="text-[13px] text-[var(--ink3)] mt-0.5" data-testid="daily-nudge-explain">
              A calm morning reminder of what needs attention today and what Heldall is holding for later.
            </p>

            {iosInstall ? (
              <p className="text-[13px] text-[var(--ink2)] mt-3 rounded-xl bg-[var(--surface)] border border-[var(--line2)] px-3 py-2.5">
                On iPhone: add Heldall to your Home Screen first, then open it from there to enable this.
              </p>
            ) : !push.supported ? (
              <p className="text-[13px] text-[var(--ink3)] mt-3">This browser doesn't support notifications.</p>
            ) : nudgeOn ? (
              <div className="mt-3 flex items-center gap-2.5" data-testid="daily-nudge-on">
                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[var(--pine)] bg-[var(--pine-soft)] rounded-full px-2 py-0.5">
                  <Check size={11} strokeWidth={2.4} /> On
                </span>
                <span className="text-[12px] text-[var(--ink4)]">Each morning</span>
                <button data-testid="daily-nudge-disable-btn" onClick={disableNudge} disabled={busy}
                  className="ml-auto text-[13px] font-semibold text-[var(--ink3)] hover:text-[var(--ink2)] transition-colors disabled:opacity-50">
                  Turn off
                </button>
              </div>
            ) : (
              <button data-testid="daily-nudge-enable-btn" onClick={enableNudge} disabled={busy}
                className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] text-[var(--on-pine)] px-4 py-2 text-sm font-semibold transition-all disabled:opacity-50 shadow-[0_4px_14px_rgba(44,90,77,0.20)]">
                <Bell size={14} strokeWidth={2} /> Turn on
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Handoff-completion push — sender opt-in. Only for two-person households. */}
      {hasPartner && (
        <div className="mt-4 rounded-2xl border border-[var(--line2)] bg-[var(--surface2)] p-4" data-testid="handoff-push-section">
          <div className="flex items-start gap-3">
            <span className="h-9 w-9 rounded-full bg-[var(--pine-soft)] flex items-center justify-center text-[var(--pine)] shrink-0">
              <PackageCheck size={17} strokeWidth={1.9} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-semibold text-[var(--ink)]">Tell me when my partner handles something</p>
              <p className="text-[13px] text-[var(--ink3)] mt-0.5" data-testid="handoff-push-explain">
                Heldall can let you know when {partnerName} marks something you handed over as done, so you can stop thinking about it.
              </p>

              {iosInstall ? (
                <p className="text-[13px] text-[var(--ink2)] mt-3 rounded-xl bg-[var(--surface)] border border-[var(--line2)] px-3 py-2.5">
                  On iPhone: add Heldall to your Home Screen first, then open it from there to enable this.
                </p>
              ) : !push.supported ? (
                <p className="text-[13px] text-[var(--ink3)] mt-3">This browser doesn't support notifications.</p>
              ) : handoffOn ? (
                <div className="mt-3 flex items-center gap-2.5" data-testid="handoff-push-on">
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[var(--pine)] bg-[var(--pine-soft)] rounded-full px-2 py-0.5">
                    <Check size={11} strokeWidth={2.4} /> On
                  </span>
                  <button data-testid="handoff-push-disable-btn" onClick={disableHandoff} disabled={busy}
                    className="text-[13px] font-semibold text-[var(--ink3)] hover:text-[var(--ink2)] transition-colors disabled:opacity-50">
                    Turn off
                  </button>
                </div>
              ) : (
                <button data-testid="handoff-push-enable-btn" onClick={enableHandoff} disabled={busy}
                  className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] text-[var(--on-pine)] px-4 py-2 text-sm font-semibold transition-all disabled:opacity-50 shadow-[0_4px_14px_rgba(44,90,77,0.20)]">
                  <Bell size={14} strokeWidth={2} /> Enable
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
